// src/composables/usePushNotifications.js
//
// PRD-022 INC-2 — opt-in de push (FCM-for-Web) + diagnóstico por aparelho.
//
// Fluxo enable(): Notification.requestPermission() → getToken(messaging, {vapidKey,
// serviceWorkerRegistration}) → registerWebPushToken (core-api). disable() revoga
// (deleteToken + unregister). sendTest() chama o endpoint de teste.
//
// LGPD: opt-in explícito (o usuário aciona). iOS (CA-11): push só após o PWA
// instalado — enable() recusa em iOS não-standalone e o diagnóstico comunica.
//
// Defensivo: sem suporte/sem config → enable() retorna false com mensagem, nunca lança.

import { ref, computed } from "vue";
import { getToken, deleteToken, onMessage } from "firebase/messaging";
import {
  getMessagingInstance,
  VAPID_KEY,
  isFirebaseConfigured,
  firebaseConfig,
} from "@/config/firebase";
import {
  registerWebPushToken,
  unregisterWebPushToken,
  sendTestPush,
} from "@/services/pushTokenService";
import { usePwaInstall } from "@/composables/usePwaInstall";
import { logger } from "@/utils/logger";

const SUBSCRIBED_KEY = "push_subscribed";
const TOKEN_KEY = "push_token";
// Convite de ativação adiado — ver `deferInvite`.
const INVITE_DEFER_KEY = "push_invite_deferred_until";
// "Não quero ser lembrado" (BUG-051). Sem isto, a única saída era adiar, e o
// convite voltava sozinho: quem já tinha decidido não ativar era perguntado de
// novo para sempre. Pedido insistente sem porta de saída é o que faz a pessoa
// aprender a fechar sem ler — inclusive o pedido seguinte, que importa.
const INVITE_OPTOUT_KEY = "push_invite_opted_out";
// "Mais tarde" — dentro do turno, não daqui a uma semana.
const DEFER_LATER_MS = 4 * 60 * 60 * 1000;
const SW_URL = `${process.env.BASE_URL || "/"}firebase-messaging-sw.js`;
// Escopo próprio (convenção do Firebase) — NÃO `/`, senão colide com o sw.js de
// cache (INC-1) que controla `/`; registrar ambos em `/` faz um substituir o
// outro e quebra o getToken ("não consegue registrar").
const FCM_SW_SCOPE = "/firebase-cloud-messaging-push-scope";

function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function removeLocal(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readInviteDeferred() {
  try {
    const raw = localStorage.getItem(INVITE_DEFER_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (Number.isNaN(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
}

// Amanhã 00:00 no fuso do aparelho. Quem diz "hoje não" espera ser perguntado
// AMANHÃ — 24h corridas a partir das 22h30 empurrariam para depois de amanhã.
function proximaViradaDeDia() {
  const amanha = new Date();
  amanha.setHours(24, 0, 0, 0);
  return amanha.getTime();
}

// ESTADO COMPARTILHADO ENTRE TODAS AS INSTÂNCIAS (BUG-051).
//
// Cada chamada de usePushNotifications() criava refs próprias: o convite e o card
// de Configurações eram dois estados independentes sobre o mesmo fato. Ativar
// push nas Configurações não escondia o convite; o "Agora não" do convite não
// chegava ao card. Duas partes da mesma tela discordando.
//
// Mesmo padrão de singleton de `useQueueStats`. As refs são re-hidratadas do
// localStorage a cada chamada (`sincronizarComStorage`) porque o localStorage é
// a fonte da verdade que sobrevive ao reload — ler só na carga do módulo deixaria
// o estado preso ao primeiro instante da sessão.
const subscribed = ref(false);
const inviteDeferred = ref(false);
const inviteOptedOut = ref(false);
const permission = ref("default");
const busy = ref(false);
const lastError = ref(null);

function sincronizarComStorage() {
  subscribed.value = readLocal(SUBSCRIBED_KEY) === "true";
  inviteDeferred.value = readInviteDeferred();
  inviteOptedOut.value = readLocal(INVITE_OPTOUT_KEY) === "true";
  permission.value =
    typeof Notification !== "undefined" ? Notification.permission : "default";
}

export function usePushNotifications() {
  const { isStandalone, isIos, isMobile } = usePwaInstall();

  sincronizarComStorage();

  const supported = computed(
    () =>
      typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      isFirebaseConfigured(),
  );
  const secureContext = computed(
    () => typeof window !== "undefined" && window.isSecureContext === true,
  );

  // iOS exige PWA instalado p/ push (CA-11 / F-14).
  const iosNeedsInstall = computed(() => isIos && !isStandalone.value);

  // Convite recorrente de ATIVAÇÃO de push. Aparece quando: push suportado, ainda
  // não inscrito, permissão não negada (re-pedir não reabre o diálogo do navegador),
  // não adiado (TTL 7 dias) e NÃO no mobile não-instalado (lá o convite de
  // instalação fala primeiro; iOS exige instalar antes). → desktop ou já instalado.
  const shouldInvite = computed(
    () =>
      supported.value &&
      !subscribed.value &&
      permission.value !== "denied" &&
      !inviteDeferred.value &&
      !inviteOptedOut.value &&
      (isStandalone.value || !isMobile),
  );

  /**
   * Adia o convite.
   *
   * @param {"later"|"today"} escopo
   *   `later` = 4h, dentro do turno: quem está no meio de um atendimento não
   *   quer decidir agora, mas decide hoje.
   *   `today` = até a virada do dia. Não são 24h corridas de propósito — às
   *   22h30, 24h corridas empurrariam a pergunta para depois de amanhã, e quem
   *   diz "hoje não" espera ser perguntado amanhã.
   */
  function deferInvite(escopo = "later") {
    const ate =
      escopo === "today" ? proximaViradaDeDia() : Date.now() + DEFER_LATER_MS;
    writeLocal(INVITE_DEFER_KEY, String(ate));
    inviteDeferred.value = true;
  }

  /** "Não quero ser lembrado" — some para sempre. Só as Configurações trazem de volta. */
  function optOutInvite() {
    writeLocal(INVITE_OPTOUT_KEY, "true");
    inviteOptedOut.value = true;
  }

  /**
   * Desfaz o opt-out.
   *
   * Chamado no `enable()` bem-sucedido: se a pessoa ativou push — pelo convite ou
   * pelas Configurações — ela mudou de ideia, e deixar o opt-out gravado faria o
   * convite sumir para sempre caso ela desativasse depois. A saída não pode ser
   * um beco sem retorno.
   */
  function clearInviteOptOut() {
    removeLocal(INVITE_OPTOUT_KEY);
    inviteOptedOut.value = false;
  }

  const diagnosis = computed(() => ({
    configured: isFirebaseConfigured(),
    supported: supported.value,
    secureContext: secureContext.value,
    permission: permission.value,
    subscribed: subscribed.value,
    iosNeedsInstall: iosNeedsInstall.value,
  }));

  async function resolveSwRegistration() {
    if (!("serviceWorker" in navigator)) return undefined;
    try {
      // Config pública injetada na query string — única fonte = .env.local
      // (o SW estático não lê process.env). Ver firebase-messaging-sw.js.
      const params = new URLSearchParams({
        apiKey: firebaseConfig.apiKey || "",
        authDomain: firebaseConfig.authDomain || "",
        projectId: firebaseConfig.projectId || "",
        messagingSenderId: firebaseConfig.messagingSenderId || "",
        appId: firebaseConfig.appId || "",
      });
      return await navigator.serviceWorker.register(`${SW_URL}?${params.toString()}`, {
        scope: FCM_SW_SCOPE,
      });
    } catch (err) {
      logger.error("[push] falha ao registrar firebase-messaging-sw.js:", err);
      return undefined;
    }
  }

  async function fetchToken() {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;
    const serviceWorkerRegistration = await resolveSwRegistration();
    // VAPID opcional: sem ela, o SDK usa a DEFAULT_VAPID_KEY embutida.
    const options = { serviceWorkerRegistration };
    if (VAPID_KEY) options.vapidKey = VAPID_KEY;
    return getToken(messaging, options);
  }

  function wireForegroundMessages(messaging) {
    if (!messaging) return;
    onMessage(messaging, (payload) => {
      logger.debug("[push] mensagem em foreground", payload?.notification?.title);
      const n = payload?.notification;
      if (n && typeof Notification !== "undefined" && Notification.permission === "granted") {
        // eslint-disable-next-line no-new
        new Notification(n.title || "Notificação", { body: n.body || "" });
      }
    });
  }

  async function enable() {
    lastError.value = null;
    if (!supported.value) {
      lastError.value = isFirebaseConfigured()
        ? "Notificações não são suportadas neste navegador."
        : "Push ainda não está configurado neste ambiente.";
      return false;
    }
    if (iosNeedsInstall.value) {
      lastError.value =
        "No iPhone/iPad, instale o app primeiro (Compartilhar → Adicionar à Tela de Início) e abra pelo ícone.";
      return false;
    }

    busy.value = true;
    try {
      const perm = await Notification.requestPermission();
      permission.value = perm;
      if (perm !== "granted") {
        lastError.value = "Permissão de notificações negada.";
        return false;
      }

      const token = await fetchToken();
      if (!token) {
        lastError.value = "Não foi possível obter o token de push.";
        return false;
      }

      await registerWebPushToken(token);
      writeLocal(TOKEN_KEY, token);
      writeLocal(SUBSCRIBED_KEY, "true");
      subscribed.value = true;
      clearInviteOptOut();

      wireForegroundMessages(await getMessagingInstance());
      return true;
    } catch (err) {
      logger.error("[push] falha ao ativar notificações:", err);
      lastError.value = "Erro ao ativar notificações. Tente novamente.";
      return false;
    } finally {
      busy.value = false;
    }
  }

  async function disable() {
    busy.value = true;
    lastError.value = null;
    try {
      const token = readLocal(TOKEN_KEY);
      const messaging = await getMessagingInstance();
      if (messaging) {
        try {
          await deleteToken(messaging);
        } catch (err) {
          logger.warn("[push] deleteToken falhou (seguindo):", err);
        }
      }
      if (token) {
        try {
          await unregisterWebPushToken(token);
        } catch (err) {
          logger.warn("[push] unregister falhou (seguindo):", err);
        }
      }
      writeLocal(SUBSCRIBED_KEY, "false");
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch {
        /* ignore */
      }
      subscribed.value = false;
      return true;
    } finally {
      busy.value = false;
    }
  }

  async function sendTest() {
    lastError.value = null;
    busy.value = true;
    try {
      await sendTestPush();
      return true;
    } catch (err) {
      lastError.value = err?.message || "Falha ao enviar push de teste.";
      return false;
    } finally {
      busy.value = false;
    }
  }

  return {
    supported,
    secureContext,
    permission,
    subscribed,
    busy,
    lastError,
    iosNeedsInstall,
    shouldInvite,
    inviteOptedOut,
    diagnosis,
    enable,
    disable,
    deferInvite,
    optOutInvite,
    clearInviteOptOut,
    sendTest,
  };
}
